import { Button, Layout, Modal, Spacer, SvgSparkle, Text } from '@loomhq/lens';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { INFO } from 'constants/error-severities';
import {
  RECORDING_LIMIT_MODAL_CLICKED,
  RECORDING_LIMIT_MODAL_DISMISSED,
  RECORDING_LIMIT_MODAL_SHOWN,
  RECORDING_LIMIT_TOAST_CLICKED,
} from 'constants/events';
import { PLANS_AND_BILLING } from 'constants/routes';
import { track } from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';
import * as logger from 'utilities/loggerx';

import UpdateUserReachedRecordingLimitChecklistItem from './updateUserReachedRecordingLimitChecklistItem.gql';
import { showConfirmationModal } from '../../../actions/confirmationModal';

const RecordingTimerPaywallModal = ({
  user,
  renderToast,
  videoDurationLimit,
}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(!renderToast);

  const analyticsData = useMemo(() => {
    return {
      userId: window.loomSSRUser.id,
      userEmail: window.loomSSRUser.email,
    };
  }, []);

  const videoDurationLimitToString = videoDurationLimit
    ? videoDurationLimit < 60
      ? `${videoDurationLimit} second`
      : `${videoDurationLimit / 60} minute`
    : '';

  useEffect(() => {
    if (!showModal) {
      return;
    }

    const updateChecklistItem = async () => {
      const gqlClient = getGraphQLClient();

      try {
        await gqlClient.mutate({
          mutation: UpdateUserReachedRecordingLimitChecklistItem,
          variables: {
            userId: user.id,
          },
        });
      } catch (err) {
        logger.warning(err, { userId: user.id }, { team: logger.COMPANY_TEAM });
      }
    };

    track(RECORDING_LIMIT_MODAL_SHOWN, analyticsData);
    updateChecklistItem();
  }, [analyticsData, showModal, user.id]);

  useEffect(() => {
    if (!renderToast) {
      return;
    }

    const toastMessage = (
      <Layout gap="small">
        <Layout.Section width={36}>
          <Text size="small" fontWeight="bold">
            You&apos;ve hit the {videoDurationLimitToString} recording limit.
          </Text>
          <Text size="small">
            If you have more to add, upgrade for unlimited recording time.
          </Text>
        </Layout.Section>
        <Layout.Section>
          <Button
            alignment="center"
            variant="upgrade"
            icon={<SvgSparkle />}
            onClick={() => {
              track(RECORDING_LIMIT_TOAST_CLICKED, analyticsData, () => {
                window.location.href = PLANS_AND_BILLING;
              });
            }}
          >
            Upgrade
          </Button>
        </Layout.Section>
      </Layout>
    );

    dispatch(showConfirmationModal(toastMessage, INFO, 6000));
  }, [analyticsData, dispatch, renderToast, videoDurationLimitToString]);

  const onClose = () => {
    track(RECORDING_LIMIT_MODAL_DISMISSED, analyticsData);
    setShowModal(false);
  };

  return (
    <Modal
      isOpen={showModal}
      onCloseClick={onClose}
      onBackgroundClick={onClose}
      maxWidth={55}
    >
      <Text fontWeight="medium" alignment="center" size="large">
        You&apos;ve got a lot to say!
      </Text>
      <Spacer bottom={2} />
      <Text size="small" alignment="center" color="bodyDimmed">
        You&apos;ve hit the {videoDurationLimitToString} recording limit. If you
        have more to add, upgrade for unlimited recording time.
      </Text>
      <Spacer bottom={2} />
      <Button
        variant="upgrade"
        size="large"
        onClick={() => {
          track(RECORDING_LIMIT_MODAL_CLICKED, analyticsData, () => {
            window.location.href = PLANS_AND_BILLING;
          });
        }}
        icon={<SvgSparkle />}
      >
        Upgrade for Unlimited Recordings
      </Button>
      <Spacer bottom={2} />
      <Text size="small" alignment="center" onClick={onClose}>
        Go to my video
      </Text>
    </Modal>
  );
};

export default RecordingTimerPaywallModal;
